import { fetchAuthSession, JWT } from '@aws-amplify/auth';
import { useMutation } from '@tanstack/react-query';
import * as React from 'react';
import { trackDatadogSessionIssue } from '../../../datadogUserTracking';
import isCancelledRequestError from '../../../utils/isCancelledRequestError';

const REFRESH_RETRY_DELAYS_MS = [2000, 5000, 10000, 30000, 60000];

export type AmplifyTokens = { idToken: JWT, accessToken: JWT };

async function fetchRequiredAuthTokens(): Promise<AmplifyTokens> {
  const authSession = await fetchAuthSession({ forceRefresh: true });
  const idToken = authSession.tokens?.idToken;
  const accessToken = authSession.tokens?.accessToken;

  if (!idToken || !accessToken) {
    throw new Error('Missing Amplify auth tokens');
  }

  return { idToken, accessToken };
}

function getRefreshRetryDelay(attempt: number) {
  return REFRESH_RETRY_DELAYS_MS[Math.min(attempt, REFRESH_RETRY_DELAYS_MS.length - 1)];
}

function getRefreshDelay(tokens: AmplifyTokens) {
  const exp = Math.min(tokens.idToken.payload.exp!, tokens.accessToken.payload.exp!);
  const now = Math.floor(Date.now() / 1000);
  const timeUntilExpiration = exp - now;

  return (timeUntilExpiration / 2) * 1000;
}

function useRefreshSchedule(requestRefresh: () => void) {
  const scheduledRefreshRef = React.useRef<NodeJS.Timeout | null>(null);
  const scheduledRecoveryRef = React.useRef<NodeJS.Timeout | null>(null);
  const recoveryAttemptRef = React.useRef(0);

  const clearScheduledRefresh = React.useCallback(() => {
    if (scheduledRefreshRef.current) {
      clearTimeout(scheduledRefreshRef.current);
      scheduledRefreshRef.current = null;
    }
  }, []);

  const clearScheduledRecovery = React.useCallback(() => {
    if (scheduledRecoveryRef.current) {
      clearTimeout(scheduledRecoveryRef.current);
      scheduledRecoveryRef.current = null;
    }
  }, []);

  const resetRecovery = React.useCallback(() => {
    clearScheduledRecovery();
    recoveryAttemptRef.current = 0;
  }, [clearScheduledRecovery]);

  const scheduleRefreshBeforeExpiry = React.useCallback((tokens: AmplifyTokens) => {
    clearScheduledRefresh();
    scheduledRefreshRef.current = setTimeout(requestRefresh, getRefreshDelay(tokens));
  }, [clearScheduledRefresh, requestRefresh]);

  const scheduleRecoveryRetry = React.useCallback(() => {
    clearScheduledRefresh();
    clearScheduledRecovery();

    const retryDelay = getRefreshRetryDelay(recoveryAttemptRef.current);
    recoveryAttemptRef.current += 1;
    scheduledRecoveryRef.current = setTimeout(requestRefresh, retryDelay);
  }, [clearScheduledRefresh, clearScheduledRecovery, requestRefresh]);

  const recoverImmediatelyIfWaiting = React.useCallback(() => {
    if (!scheduledRecoveryRef.current) {
      return;
    }

    clearScheduledRecovery();
    requestRefresh();
  }, [clearScheduledRecovery, requestRefresh]);

  const refreshNow = React.useCallback(() => {
    clearScheduledRecovery();
    requestRefresh();
  }, [clearScheduledRecovery, requestRefresh]);

  React.useEffect(() => () => {
    clearScheduledRefresh();
    clearScheduledRecovery();
  }, [clearScheduledRefresh, clearScheduledRecovery]);

  return React.useMemo(() => ({
    resetRecovery,
    recoverImmediatelyIfWaiting,
    refreshNow,
    scheduleRefreshBeforeExpiry,
    scheduleRecoveryRetry,
  }), [
    recoverImmediatelyIfWaiting,
    refreshNow,
    resetRecovery,
    scheduleRefreshBeforeExpiry,
    scheduleRecoveryRetry,
  ]);
}

export default function useAmplifyTokenRefresh() {
  const [tokens, setTokens] = React.useState<AmplifyTokens | null>(null);
  const [isRecovering, setIsRecovering] = React.useState(false);
  const refreshTokenMutationRef = React.useRef<(() => void) | null>(null);

  const requestRefresh = React.useCallback(() => {
    setIsRecovering(false);
    refreshTokenMutationRef.current?.();
  }, []);

  const refreshSchedule = useRefreshSchedule(requestRefresh);

  const refreshTokenMutation = useMutation<AmplifyTokens, Error, void>(
    ['refresh-token'],
    fetchRequiredAuthTokens,
    {
      retry(failureCount, error) {
        return isCancelledRequestError(error) && failureCount < 3;
      },
      onSuccess(nextTokens) {
        refreshSchedule.resetRecovery();
        setTokens(nextTokens);
      },
      onError(error) {
        console.error('Error refreshing token:', error);
        trackDatadogSessionIssue('bad_expiry');
        setTokens(null);
        setIsRecovering(true);
        refreshSchedule.scheduleRecoveryRetry();
      },
    },
  );

  React.useEffect(() => {
    refreshTokenMutationRef.current = refreshTokenMutation.mutate;
  }, [refreshTokenMutation.mutate]);

  React.useEffect(() => {
    refreshTokenMutation.mutate();
  }, []);

  React.useEffect(() => {
    window.addEventListener('online', refreshSchedule.recoverImmediatelyIfWaiting);
    return () => {
      window.removeEventListener('online', refreshSchedule.recoverImmediatelyIfWaiting);
    };
  }, [refreshSchedule.recoverImmediatelyIfWaiting]);

  React.useEffect(() => {
    if (!tokens) {
      return undefined;
    }

    refreshSchedule.scheduleRefreshBeforeExpiry(tokens);
    return undefined;
  }, [refreshSchedule, tokens]);

  return {
    tokens,
    error: isRecovering ? null : refreshTokenMutation.error,
    isLoading: refreshTokenMutation.isLoading,
    reset: refreshTokenMutation.reset,
    refresh: refreshSchedule.refreshNow,
  };
}
